import type { TelErrorData } from './tel-error-data'
import type { TelStorage, TelStorageBindable } from './tel-storage'
import type { _createMetricsFactory } from './tel-metrics'
import type { MetricPoint } from './tel-metrics-types'
import { ulid } from '@w/utils'
import type { Attrs } from './attrs'

// tel - application-facing telemetry context. Pass it through every API.
export interface Tel {
  // logs
  info: (body: unknown, attrs?: Attrs) => void
  audit: (body: unknown, attrs?: Attrs) => void
  error<T>(value: T, attrs?: Attrs): T
  warn: (body: unknown, attrs?: Attrs) => void
  debug: (body: unknown, attrs?: Attrs) => void

  // traces
  readonly span: TelSpan
  readonly currentSpan: Span

  flush(): Promise<void>

  _createSpan(opts: InstrumentedSpanOpts): Span
  _createTel(opts: InstrumentedSpanOpts): Tel // same as createSpan but returns tel
  /**
   * Instrumentation-only. Serializes the ended request unit for transport to the hub.
   * :uc[boundary-export-serializes-request-unit]
   */
  _exportBoundary(): BoundaryTelemetry
  /**
   * Instrumentation-only. Inserts foreign telemetry under the current span.
   * :uc[boundary-insert-nests-under-current-span]
   */
  _insertBoundaryTrace(
    trace: TraceData,
    metrics: MetricPoint[],
    resource?: BoundaryTelemetry['resource'],
    completion?: { preserveParent?: boolean; error?: unknown; updateExisting?: boolean; dedupeBySpan?: boolean },
  ): void
  _getBaggage(): Baggage

  _clear(): void

  // Copy-on-write user info: reads from inherited store until setUserInfo detaches
  setUserInfo(info: Partial<ResourceUserInfo>): void
  getUserInfo(): ResourceUserInfo
}

export type ContextPropagation = 'native' | 'w3c'

export interface TelSpan {
  <R>(opts: StartSpanOpts, cb: (tel: Tel) => R): Promise<Awaited<R>>
}

type RootMetricFactories = Pick<
  ReturnType<typeof _createMetricsFactory>,
  'counter' | 'upDownCounter' | 'gauge' | 'histogram' | 'exponentialHistogram'
>

// RootTel extends Tel with metric factories and storage
export type RootTel = Tel &
  RootMetricFactories & {
    _storage: TelStorage
    resource: TelResources
    setHandlers(...handlers: TelStorageBindable[]): void
    /** Flush + clear all aggregator intervals. Use in batch jobs to allow process exit. */
    shutdown(): Promise<void>
  }

export type StartSpanOpts = Pick<
  SpanData,
  | 'name'
  | 'type'
  | 'displayName'
  | 'target'
  | 'label'
  | 'performanceMetricId'
  | 'attributes'
> & {
  input: unknown
  alreadyMasked?: boolean
  trace?: true
}

/** Internal instrumentation may create a service-local request boundary. */
export type InstrumentedSpanOpts = Omit<StartSpanOpts, 'trace'> &
  Partial<
    Pick<
    SpanData,
    | 'id'
    | 'parentId'
    | 'traceId'
    | 'requestId'
    | 'sessionId'
    | 'traceFlags'
    | 'traceState'
    | 'baggage'
    | 'otel'
    >
  > & {
    userInfo?: Partial<ResourceUserInfo>
    trace?: boolean
    request?: true
    worker?: boolean
  }

type Ulid = string

export interface Span {
  data: SpanData
  // attributes will be merged with existing attributes
  end(
    data: Pick<SpanData, 'output' | 'attributes'> & {
      error?: unknown
      name?: string
    },
    opts?: { errorLevel?: 'warning' | 'error'; dedupeBySpan?: boolean },
  ): void
  update(patch: Partial<SpanData>): void

  _getBaggage(): Baggage
}

export const _originArray = ['server', 'client'] as const
export type Origin = (typeof _originArray)[number]

export const _spanTypeArray = ['query', 'mutation', 'subscription'] as const
export type SpanType = (typeof _spanTypeArray)[number]
export const _spanTargetArray = [
  'db',
  'sf',
  'bucket',
  'fetch',
  'rpc-handler',
  'server-handler',
  'job',
  'router',
  '3rdParty',
  'broker',
] as const
export type SpanTarget = (typeof _spanTargetArray)[number]
export const _spanStatusArray = ['ok', 'error', 'warning'] as const
export type SpanStatus = (typeof _spanStatusArray)[number]

// these props are inherited and propagated
export interface Baggage {
  parentId?: Ulid // native parent span id; separate from remote otel.parentSpanId
  traceId?: Ulid // native trace group id
  requestId?: Ulid // service-local request boundary id; never transported
  sessionId?: Ulid // links all spans to user information and client version (eg client might be outdated and it can cause errors)
  traceFlags?: number // 0x00=not sampled, 0x01=sampled (advisory for downstream)
  traceState?: string // vendor k=v,k=v (pass-through for W3C compat)
  baggage?: string // W3C baggage header value. Propagate unchanged.
  otel?: {
    traceId: string // W3C 32-hex trace id
    parentSpanId?: string // W3C 16-hex remote parent span id
  }
}

/** Context allowed to cross a service boundary. */
export type TransportBaggage = Omit<Baggage, 'requestId'>

export interface UserAgent {
  browser: {
    name?: string
    version?: string
  }
  engine: {
    name?: string
    version?: string
  }
  os: {
    name?: string
    version?: string
  }
}

export const TraceFlags = {
  NONE: 0x00,
  SAMPLED: 0x01,
} as const
export type TraceFlags = (typeof TraceFlags)[keyof typeof TraceFlags]

export interface TelDeployResource {
  name: string
  version: string
  env: string
  origin: Origin
}

export interface TelMetricResource {
  name: string
  env: string
  origin: Origin
}

export interface TelResources {
  deploy: TelDeployResource
  metrics: TelMetricResource
}

export type TelAlert<K extends string = string, D = unknown> = {
  kind: K
  data: D
  timestamp: number
  message: string
  resource: TelDeployResource
  traceId?: string
  spanId?: string
  requestId?: string
  attributes?: Attrs
}

export type TelAlertHandler<A extends TelAlert = TelAlert> = (
  alert: A,
) => void | Promise<void>

// set on rootSpan only
export interface ResourceUserInfo {
  userAgent?: UserAgent
  userId?: string
  companyId?: string
}

export type TelParent = Omit<Baggage, 'parentId'> & {
  id?: string
  performanceMetricId?: string
}
// spans describe an asynchronous operation (its duration, input, output, error). spans are foundation of observability.
export interface SpanData extends Baggage, ResourceUserInfo {
  previousId?: Ulid // when request travels through a system traces can be terminated and linked using this field
  // nextId?: Ulid // kinda makes no sense. use previousId // when user navigates between different pages in one session link them using these two fields. first navigation links to session span

  id: Ulid
  name: string // ideally it should be low cardinality
  type?: SpanType
  displayName?: string // high cardinality name eg inlinePath(routePath, params)

  performanceMetricId?: string

  label?: string // eg 3rd party service name, or a unique key to track apdex
  target?: SpanTarget

  // automatically set
  origin?: Origin
  worker?: boolean

  duration?: number
  timestamp?: number

  input?: unknown
  inputSize?: number
  output?: unknown
  outputSize?: number
  error?: TelErrorData
  fingerprint?: string

  status?: SpanStatus

  attributes?: Attrs
}
export function getBaggage(span: SpanData): Baggage {
  return {
    traceId: span.traceId,
    requestId: span.requestId,
    sessionId: span.sessionId,
    parentId: span.id === 'root' ? undefined : span.id,
    traceFlags: span.traceFlags,
    traceState: span.traceState,
    baggage: span.baggage,
    otel: span.otel
      ? {
          traceId: span.otel.traceId,
        }
      : undefined,
  }
}
export function getLogBaggage(span: SpanData): Baggage {
  return {
    ...getBaggage(span),
    parentId: span.id === 'root' ? undefined : span.id,
  }
}

// spans can have logs. logs provide additional information about asynchronous operations.
export interface LogData extends Baggage {
  id: Ulid
  body: unknown

  level: LogLevel

  origin?: Origin
  worker?: boolean

  error?: TelErrorData
  fingerprint?: string

  // Actor attribution for error logs — servers count affected users per
  // error group from the log payload, so error logs must carry these.
  userId?: string
  companyId?: string

  attributes?: Attrs
}

export const ALWAYS_EXPORT_LOG_ATTRIBUTE = '__alwaysExport__'

// Users always set `true`; exporters/ingestion rewrite it to `"name env"`,
// so the check must accept both forms.
export function shouldAlwaysExportLog(log: Pick<LogData, 'attributes'>) {
  return Boolean(log.attributes?.[ALWAYS_EXPORT_LOG_ATTRIBUTE])
}

/** Exported (wire/db) value of the audit marker — enables subscription matching by attribute value. */
export function formatAlwaysExportValue(deploy: {
  name: string
  env: string
}): string {
  return `${deploy.name} ${deploy.env}`
}

export const _logLevelArray = [
  'trace',
  'debug',
  'info',
  'warn',
  'error',
  'fatal',
] as const
export type LogLevel = (typeof _logLevelArray)[number]

export interface TraceData extends SpanData {
  logs: LogData[]
  children: TraceData[]
}

/** Serializable telemetry captured at a non-hub boundary (RPC server request, worker call). */
export interface BoundaryTelemetry {
  trace: TraceData
  metrics: MetricPoint[]
  resource: Pick<TelDeployResource, 'name' | 'env' | 'origin'> & {
    version?: string
  }
}

// Type that removes 'id' from telemetry data types
type WithoutId<T> = Omit<T, 'id'>

// Type for creating TraceData with proper ID stripping
type CreateTraceDataInput = Omit<TraceData, 'events' | 'logs' | 'children'> & {
  logs?: WithoutId<LogData>[]
  children?: CreateTraceDataInput[]
}

export function _createFakeTraceData(
  overrides: Partial<CreateTraceDataInput> = {},
): TraceData {
  const { logs = [], children = [], ...spanDataOverrides } = overrides

  // Create base span data with overrides first to get the span ID
  const baseSpanData: SpanData = {
    id: ulid(),
    name: '',
    traceId: spanDataOverrides.traceId ?? ulid(),

    ...spanDataOverrides,
  }

  // Add 'id' and propagate baggage to logs
  // oxlint-disable-next-line oxc/no-map-spread -- test-helper, clarity > perf
  const processedLogs: LogData[] = logs.map((logData) => ({
    ...logData,
    id: ulid(), // Generate new id
    parentId: baseSpanData.id,
    traceId: baseSpanData.traceId,
    requestId: baseSpanData.requestId,
    sessionId: baseSpanData.sessionId,
    baggage: baseSpanData.baggage,
  }))

  // Process children recursively and associate with the span, propagating baggage
  const processedChildren: TraceData[] = children.map((child) => {
    return _createFakeTraceData({
      ...child,
      parentId: baseSpanData.id, // Associate with parent span
      traceId: baseSpanData.traceId,
      requestId: baseSpanData.requestId,
      sessionId: baseSpanData.sessionId,
      baggage: baseSpanData.baggage,
    })
  })

  return {
    ...baseSpanData,
    logs: processedLogs,
    children: processedChildren,
  }
}
